/**
 * PulseTab — iter86.20 phase 1 + iter86.25. Crew Briefings: LLM release
 * summaries (Claude Sonnet, temp 0, evidence-cited) scoped to the viewer's
 * domain subscriptions, with non-gating Approve/Investigate/Reject signals,
 * review-state tabs, per-section evidence receipts, a "How to read this"
 * explainer, an audit view, and an LLM usage/circuit-breaker card.
 * Governed by /app/memory/pulse_registry.md.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const DOMAIN_LABELS = {
    general: "General Summary", release_ops: "Release & Ops",
    operations: "Operations Views", governance: "Governance & Impact",
    vendors_obligations: "Vendors & Obligations",
    product_features: "Product & Features", dev_agency_audit: "Dev Agency Audit",
};
const SIGNAL_STYLES = {
    approve: "bg-emerald-900/40 text-emerald-300 border-emerald-500",
    investigate: "bg-amber-900/40 text-amber-300 border-amber-500",
    reject: "bg-red-900/40 text-red-300 border-red-500",
};

// iter86.25 — review-state buckets over the viewer's per-section signals.
// iter86.38 — steward request: start on "Not reviewed"; "All" moves last.
const REVIEW_STATES = [
    ["not_reviewed", "Not reviewed"],
    ["pending", "Pending"],       // signalled "investigate"
    ["accepted", "Accepted"],     // signalled "approve"
    ["rejected", "Rejected"],
    ["all", "All"],
];
// iter86.30 — General Summary sections are read-only executive roll-ups
// (votes happen on domain sections), so they carry no review state: they
// show under "All" only and any stale pre-read-only signal is ignored.
const sectionState = (b, s) =>
    s.domain === "general" ? "exempt"
        : !s.my_signal && b?.pre_join ? "exempt"
        : !s.my_signal ? "not_reviewed"
        : s.my_signal === "approve" ? "accepted"
        : s.my_signal === "investigate" ? "pending"
        : "rejected";

// iter86.25 — the explainer the steward asked for, in-product.
function HowToReadModal({ onClose }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
            <div className="brutal-card bg-zinc-950 border border-slate-600 max-w-2xl w-full p-5 space-y-3 max-h-[85dvh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()} data-testid="pulse-info-modal">
                <div className="flex items-center justify-between">
                    <p className="font-bold text-white text-sm uppercase">How to read a briefing</p>
                    <button onClick={onClose} className="brutal-btn-ghost text-xs px-2 py-1" data-testid="pulse-info-close">✕</button>
                </div>
                <div className="space-y-3 text-xs text-slate-300">
                    <div>
                        <p className="font-bold text-sky-300 mb-1">The [E#] tags — receipts, not grades</p>
                        <p>Before the AI writes a word, the system collects a numbered list of raw facts for the release — the <span className="text-white">evidence receipts</span> (E1, E2, E3…). Every sentence must end with an [E#] pointing at the exact fact it restates: <code className="text-sky-300">[E4]</code> means "this came from receipt #4". Click <span className="text-white">Show evidence receipts</span> under any section and you can check each claim against its numbered source. The number is a position in that list — it is <span className="text-white">not</span> a severity or importance rating. A sentence the AI can't back with a receipt gets rejected by the validator.</p>
                    </div>
                    <div>
                        <p className="font-bold text-sky-300 mb-1">What "Residual risk" means (domain sections)</p>
                        <p>Risk that <span className="text-white">remains after the controls ran</span> — only what a human consciously recorded rather than fixed: a waived or denied release gate (with the signed reason), an obligation under investigation. Obligations recorded as <span className="text-white">not-applicable</span> (e.g. a vendor agreement for a product surface that isn't live yet) are clean and never appear. The AI is forbidden from inferring or predicting risk ("this change might affect billing" will never appear — there'd be no receipt for it). <span className="text-white">"None recorded this release"</span> means no one knowingly shipped past a control — not "no risk exists anywhere".</p>
                    </div>
                    <div>
                        <p className="font-bold text-sky-300 mb-1">The canon mapping — "touches …"</p>
                        <p>When a recorded risk maps to the ladder, the bullet names where it lands: <code className="text-violet-300">touches standards/monetization-tier-integrity ← values/… (expresses tcl.…)</code>. Scoping is by relevance: an area this release doesn't touch (say, payments) simply never appears — <span className="text-white">silence is a genuine zero</span>, not an omission. The gate→standard bridge is steward-editable in <code>pulse_registry.md §6</code>.</p>
                    </div>
                    <div>
                        <p className="font-bold text-sky-300 mb-1">Considerations — patterns, not per-release risk</p>
                        <p>The values and obligations engines already gate each release; Pulse watches the <span className="text-white">aggregate and the pattern</span>. Deterministic detectors flag: vendor tier pressure (projected quota burn crossing the plan tier), exposure growth against an open obligation, issues carried across 3+ briefings, the same standard warning in 3 of the last 5 briefings, and the same gate waived 2 of the last 5 promotions. Each carries a suggested action. Click <span className="text-white">Acknowledge</span> to track it as an issue in Stewardship → Pulse Dispositions (Unread → Open → Closed). If a closed issue is detected again, it resurfaces flagged and reopens as a new linked record — the old record is preserved.</p>
                    </div>
                    <div>
                        <p className="font-bold text-sky-300 mb-1">Drift trajectory — one line, computed not guessed</p>
                        <p>Each briefing saves a <span className="text-white">snapshot of every recorded risk</span> and its state. The next briefing diffs the two snapshots into a single line — <span className="text-white">INCREASED</span> (new risk or a state worsened), <span className="text-white">DECREASED</span> (resolved or improved, nothing new), <span className="text-white">MIXED</span>, or <span className="text-white">UNCHANGED</span>. The first briefing reads <span className="text-white">BASELINE</span>. The AI never touches this — it renders exactly as computed.</p>
                    </div>
                    <div>
                        <p className="font-bold text-sky-300 mb-1">Your signals</p>
                        <p>Approve / Investigate / Reject per section — non-gating audit signals. The tabs above the briefings bucket sections by your signal so nothing slips: <span className="text-white">Not reviewed → Pending (investigating) → Accepted / Rejected</span>.</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ConsiderationsBlock({ b, gated, reload }) {
    const cons = b.considerations || [];
    const tid = (key) => key.replace(/[^a-zA-Z0-9_-]/g, "_");
    const ack = async (key) => {
        try {
            await api.pulseIssueAcknowledge(b.id, key);
            toast.success("Tracked — see Stewardship → Pulse Dispositions");
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Acknowledge failed");
        }
    };
    if (!cons.length && !b.trajectory_line) return null;
    return (
        <div className="border border-sky-900 bg-sky-950/10 p-3 space-y-2" data-testid={`pulse-considerations-${b.iter}`}>
            <p className="text-[10px] font-extrabold uppercase text-sky-300">
                Considerations — aggregate shifts & patterns (computed, not AI-written)
            </p>
            {cons.length === 0 && (
                <p className="text-[11px] text-slate-500 italic" data-testid={`pulse-considerations-empty-${b.iter}`}>
                    None detected this briefing.
                </p>
            )}
            {cons.map((c) => (
                <div key={c.key} className="border-t border-slate-800 pt-1.5 text-[11px] space-y-0.5" data-testid={`pulse-consideration-${tid(c.key)}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`pill text-[9px] font-bold uppercase border ${c.kind === "aggregate_risk" ? "bg-violet-900/40 text-violet-200 border-violet-700" : "bg-amber-900/40 text-amber-200 border-amber-700"}`}>
                            {c.kind === "aggregate_risk" ? "Aggregate risk" : "Drift concern"}
                        </span>
                        <span className="font-bold text-white">{c.title}</span>
                        <div className="flex-1" />
                        {c.tracked ? (
                            <span className="text-[9px] text-emerald-300 font-bold" data-testid={`pulse-consideration-tracked-${tid(c.key)}`}>
                                Tracked — {c.tracked.status}{c.tracked.assigned_to ? ` · ${c.tracked.assigned_to}` : ""} · seen ×{c.tracked.seen_count}
                            </span>
                        ) : (
                            <button onClick={() => ack(c.key)} disabled={gated}
                                data-testid={`pulse-acknowledge-${tid(c.key)}`}
                                className="brutal-btn text-[10px] px-2 py-1 disabled:opacity-40">
                                Acknowledge
                            </button>
                        )}
                    </div>
                    <p className="text-slate-300">{c.detail}</p>
                    <p className="text-sky-200">→ {c.action_hint}</p>
                    {c.previously_closed && !c.tracked && (
                        <p className="text-[10px] text-rose-300" data-testid={`pulse-consideration-reopen-note-${tid(c.key)}`}>
                            Previously closed ({c.previously_closed.closed_reason}) by {c.previously_closed.closed_by} — acknowledging reopens it as a new linked issue (old record preserved).
                        </p>
                    )}
                </div>
            ))}
            {b.trajectory_line && (
                <p className="text-[10px] text-slate-400 border-t border-slate-800 pt-1.5" data-testid={`pulse-trajectory-${b.iter}`}>
                    {b.trajectory_line}
                </p>
            )}
        </div>
    );
}

function EvidenceReceipts({ section }) {
    const [open, setOpen] = useState(false);
    if (!section.evidence?.length) return null;
    return (
        <div>
            <button onClick={() => setOpen((v) => !v)}
                className="text-[10px] text-sky-400 underline underline-offset-2"
                data-testid={`pulse-evidence-toggle-${section.domain}`}>
                {open ? "Hide" : "Show"} evidence receipts ({section.evidence.map((e) => e.id).join(", ")})
            </button>
            {open && (
                <div className="mt-1 space-y-1 border-l-2 border-slate-700 pl-2" data-testid={`pulse-evidence-list-${section.domain}`}>
                    {section.evidence.map((e) => (
                        <div key={e.id} className="text-[10px] text-slate-400">
                            <span className="font-bold text-sky-300">[{e.id}]</span>{" "}
                            <span className="text-slate-500 uppercase">{e.kind}</span>{" "}
                            <span className="whitespace-pre-wrap">{e.text}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function BriefingSection({ b, s, signal, gated }) {
    return (
        <div className="border border-slate-700 p-3 space-y-2" data-testid={`pulse-section-${s.domain}`}>
            <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-sky-300 text-sm">{DOMAIN_LABELS[s.domain] || s.domain}</span>
                <span className="text-[10px] text-slate-500">iter{b.iter}</span>
                {!s.validated && <span className="text-[10px] px-2 border border-amber-500 text-amber-300">RAW EVIDENCE</span>}
                {s.my_signal && s.domain !== "general" && (
                    <span className={`text-[10px] px-2 py-0.5 border ${SIGNAL_STYLES[s.my_signal]}`}>
                        {s.my_signal.toUpperCase()} by you
                    </span>
                )}
                {b.pre_join && !s.my_signal && s.domain !== "general" && (
                    <span className="text-[10px] px-2 py-0.5 border border-slate-600 text-slate-400" data-testid={`pulse-prejoin-${s.domain}`}>
                        Pre-dates your access · review optional
                    </span>
                )}
            </div>
            <pre className="whitespace-pre-wrap text-xs text-slate-200 font-sans">{s.summary_md}</pre>
            <p className="text-[9px] text-slate-500" data-testid={`pulse-evidence-legend-${s.domain}`}>
                [E#] = citation pointing to the numbered evidence receipt below — a source reference, not a severity grade.
            </p>
            <EvidenceReceipts section={s} />
            {s.domain === "general" ? (
                <p className="text-[10px] text-slate-500 italic" data-testid="pulse-general-readonly">
                    Executive summary — read-only. Votes happen on the domain sections below.
                </p>
            ) : (
                <div className="flex gap-2">
                    {["approve", "investigate", "reject"].map((sig) => (
                        <button key={sig} onClick={() => signal(b.id, s.domain, sig)} disabled={gated}
                            data-testid={`pulse-signal-${sig}-${s.domain}`}
                            className={`brutal-btn text-[10px] px-2 py-1 disabled:opacity-40 ${s.my_signal === sig ? SIGNAL_STYLES[sig] : ""}`}>
                            {sig}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function PulseTab({ onBadgeChange }) {
    const [sub, setSub] = useState("briefings");
    const [reviewTab, setReviewTab] = useState("not_reviewed");
    const [showInfo, setShowInfo] = useState(false);
    const [data, setData] = useState(null);
    const [usage, setUsage] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [busy, setBusy] = useState(false);
    const [dataPlane, setDataPlane] = useState(false);

    useEffect(() => {
        api.platformEnvironment().then((r) => setDataPlane(r.mode === "data_plane")).catch(() => {});
    }, []);

    const load = useCallback(async () => {
        try {
            const d = await api.pulseBriefings();
            setData(d);
            onBadgeChange?.(d.unreviewed_count || 0);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load briefings");
        }
    }, [onBadgeChange]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (sub === "usage") api.pulseUsage().then(setUsage).catch(() => {});
        if (sub === "audit") api.pulseReviews().then((r) => setReviews(r.reviews || [])).catch(() => {});
    }, [sub]);

    const generate = async () => {
        setBusy(true);
        try {
            await api.pulseGenerate();
            toast.success("Briefing generated");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Generation failed");
        } finally { setBusy(false); }
    };

    const signal = async (briefingId, domain, sig) => {
        try {
            await api.pulseReview(briefingId, { domain, signal: sig });
            toast.success(`Recorded: ${sig}`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to record signal");
        }
    };

    const counts = { all: 0, not_reviewed: 0, pending: 0, accepted: 0, rejected: 0 };
    (data?.briefings || []).forEach((b) => b.sections.forEach((s) => {
        counts.all += 1;
        const st = sectionState(b, s);
        if (st !== "exempt") counts[st] += 1;
    }));

    return (
        <div className="space-y-4" data-testid="pulse-tab">
            {showInfo && <HowToReadModal onClose={() => setShowInfo(false)} />}
            {dataPlane && (
                <div className="border border-rose-800 rounded p-2.5 bg-rose-950/20 text-[11px] text-rose-200" data-testid="pulse-data-plane-banner">
                    🔒 Production — Pulse is read-only here. Review signals and generation happen in the preview console.
                </div>
            )}
            <div className="flex items-center gap-2">
                {["briefings", "audit", "usage"].map((k) => (
                    <button key={k} onClick={() => setSub(k)}
                        data-testid={`pulse-subtab-${k}`}
                        className={`brutal-btn text-xs px-3 py-1 ${sub === k ? "bg-volt text-black" : ""}`}>
                        {k.toUpperCase()}
                    </button>
                ))}
                <div className="flex-1" />
                <button onClick={() => setShowInfo(true)}
                    data-testid="pulse-info-btn"
                    className="brutal-btn-ghost text-xs px-3 py-1 text-sky-300 border border-sky-800">
                    How to read this
                </button>
                <button onClick={generate} disabled={busy || dataPlane}
                    data-testid="pulse-generate-btn"
                    className="brutal-btn text-xs px-3 py-1 bg-sky-700 text-white disabled:opacity-40">
                    {busy ? "Generating…" : "Generate briefing"}
                </button>
            </div>

            {sub === "briefings" && (
                <div className="space-y-4">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        {REVIEW_STATES.map(([k, label]) => (
                            <button key={k} onClick={() => setReviewTab(k)}
                                data-testid={`pulse-review-tab-${k}`}
                                className={`brutal-btn text-[10px] px-2.5 py-1 ${reviewTab === k ? "bg-volt text-black" : ""}`}>
                                {label} ({counts[k]})
                            </button>
                        ))}
                    </div>
                    {!data?.briefings?.length && (
                        <div className="brutal-card p-6 text-sm text-slate-400" data-testid="pulse-empty">
                            No briefings yet. Click "Generate briefing" to summarize the
                            latest release, or finalize a promotion (auto-generates).
                        </div>
                    )}
                    {data?.briefings?.map((b) => {
                        const visible = b.sections.filter(
                            (s) => reviewTab === "all" || sectionState(b, s) === reviewTab);
                        if (!visible.length) return null;
                        return (
                            <div key={b.id} className="brutal-card p-4 space-y-3" data-testid={`pulse-briefing-card-${b.iter}`}>
                                <div className="flex items-center gap-3">
                                    <span className="font-bold text-white">iter{b.iter}</span>
                                    <span className="text-xs text-slate-400">{(b.created_at || "").slice(0, 16).replace("T", " ")}</span>
                                    <span className="text-[10px] px-2 py-0.5 border border-slate-600 text-slate-300">{b.trigger}</span>
                                    <span className="text-[10px] text-slate-500">~${b.total_est_cost}</span>
                                    <div className="flex-1" />
                                    <button
                                        onClick={async () => {
                                            try {
                                                const r = await api.pulseMemorialize({ iter: b.iter });
                                                toast.success(`Memorialized: ${r.line}`);
                                            } catch (e) {
                                                toast.error(e?.response?.data?.detail || "Memorialize failed");
                                            }
                                        }}
                                        disabled={dataPlane}
                                        data-testid={`pulse-memorialize-btn-${b.iter}`}
                                        title="Append this iter's review roll-up (latest signal per reviewer per domain + open follow-ups) to pulse_dispositions.md"
                                        className="brutal-btn-ghost text-[10px] px-2 py-1 text-emerald-300 border border-emerald-900 disabled:opacity-40">
                                        Memorialize roll-up
                                    </button>
                                </div>
                                <ConsiderationsBlock b={b} gated={dataPlane} reload={load} />
                                {visible.map((s) => <BriefingSection key={s.domain} b={b} s={s} signal={signal} gated={dataPlane} />)}
                            </div>
                        );
                    })}
                    {data?.briefings?.length > 0 && reviewTab !== "all" && counts[reviewTab] === 0 && (
                        <div className="brutal-card p-4 text-xs text-slate-400" data-testid="pulse-review-tab-empty">
                            No sections in "{REVIEW_STATES.find(([k]) => k === reviewTab)?.[1]}".
                        </div>
                    )}
                </div>
            )}

            {sub === "audit" && (
                <div className="brutal-card p-4" data-testid="pulse-audit-view">
                    <p className="font-bold text-white text-sm mb-2">Review signals (append-only audit)</p>
                    {!reviews.length && <p className="text-xs text-slate-400">No signals recorded yet.</p>}
                    {reviews.map((r) => (
                        <div key={r.id} className="text-xs text-slate-300 border-b border-slate-800 py-1 flex gap-2">
                            <span className={`px-1 border ${SIGNAL_STYLES[r.signal]}`}>{r.signal}</span>
                            <span className="font-bold">{DOMAIN_LABELS[r.domain] || r.domain}</span>
                            <span>{r.user_email}</span>
                            <span className="text-slate-500">{(r.ts || "").slice(0, 16).replace("T", " ")}</span>
                            {r.note && <span className="italic text-slate-400">— {r.note}</span>}
                        </div>
                    ))}
                </div>
            )}

            {sub === "usage" && usage && (
                <div className="brutal-card p-4 space-y-2" data-testid="pulse-usage-card">
                    <p className="font-bold text-white text-sm">LLM usage — {usage.model} (key: {usage.key_source})</p>
                    <div className="grid grid-cols-3 gap-3 text-xs">
                        {[["hour", usage.hour], ["day", usage.day], ["30 days", usage.month_30d]].map(([label, w]) => (
                            <div key={label} className="border border-slate-700 p-2">
                                <p className="text-slate-400">{label}</p>
                                <p className="text-white">{w.calls} calls · {w.tokens} tok</p>
                                <p className="text-emerald-300">~${w.est_cost_usd}</p>
                            </div>
                        ))}
                    </div>
                    <p className={`text-xs ${usage.breaker.open ? "text-red-400 font-bold" : "text-slate-400"}`} data-testid="pulse-breaker-state">
                        Circuit breaker: {usage.breaker.open ? "OPEN — generation disabled" : "closed"} ({usage.breaker.calls_last_hour}/{usage.breaker.cap_per_hour} calls this hour)
                    </p>
                    <p className={`text-xs ${usage.soft_cap_exceeded ? "text-amber-300 font-bold" : "text-slate-500"}`}>
                        Monthly soft cap: ${usage.monthly_soft_cap_usd} {usage.soft_cap_exceeded ? "— EXCEEDED" : ""}
                    </p>
                </div>
            )}
        </div>
    );
}
